(() => {
    const TELEMETRY_NONCE_PATTERN = /^[a-f0-9]{64}$/;

    function responsePayload(value) {
        if (
            value === null
            || typeof value !== 'object'
            || Array.isArray(value)
            || typeof value.response !== 'string'
            || value.response.trim() === ''
        ) {
            return null;
        }

        return {
            response: value.response.trim(),
            requiresPremium: value.requires_premium === true,
        };
    }

    function requestErrorMessage(status, payload) {
        if (status === 401) {
            return 'Sua sessão expirou. Entre novamente para continuar a conversa.';
        }

        if (status === 403) {
            return 'Esta conversa está disponível apenas para o dono do mapa.';
        }

        if (status === 429) {
            return 'Muitas perguntas foram enviadas em pouco tempo. Aguarde um momento e tente novamente.';
        }

        if (payload?.requires_premium === true) {
            return 'Sua assinatura Premium precisa estar ativa para continuar a conversa.';
        }

        if (typeof payload?.error === 'string' && payload.error.includes('muito longa')) {
            return payload.error;
        }

        return 'Não foi possível obter uma resposta agora. Sua pergunta foi preservada para você tentar novamente.';
    }

    function appendMessage(messages, role, content) {
        const documentObject = messages.ownerDocument ?? document;
        const article = documentObject.createElement('article');
        const author = documentObject.createElement('p');
        const body = documentObject.createElement('p');

        article.className = `am-birthchart-ai-analysis__message am-birthchart-ai-analysis__message--${role}`;
        author.className = 'am-birthchart-ai-analysis__author';
        author.textContent = role === 'user' ? 'Você' : 'Assistente';
        body.textContent = content;
        article.append(author, body);
        messages.append(article);
        article.scrollIntoView?.({behavior: 'smooth', block: 'nearest'});

        return article;
    }

    function scrollMessagesToLatest(messages) {
        messages.scrollTop = messages.scrollHeight;
    }

    function deliverReplyPresented(request, telemetryNonce) {
        if (
            typeof request !== 'function'
            || !TELEMETRY_NONCE_PATTERN.test(telemetryNonce ?? '')
        ) {
            return false;
        }

        try {
            const delivery = request('/api/guidance/open-attempt', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    client_event: 'reply_presented',
                    entry_point: 'unknown',
                    telemetry_nonce: telemetryNonce,
                    type: 'birthchart',
                }),
                credentials: 'same-origin',
                keepalive: true,
                referrerPolicy: 'no-referrer',
            });
            delivery?.catch?.(() => {});

            return true;
        } catch {
            return false;
        }
    }

    function initBirthchartAIAnalysis(documentObject = document, fetchImplementation) {
        const root = documentObject?.querySelector?.('[data-am-ai-analysis]');
        if (!root) {
            return false;
        }

        const request = typeof fetchImplementation === 'function'
            ? fetchImplementation
            : (typeof globalThis.fetch === 'function' ? globalThis.fetch.bind(globalThis) : null);

        const form = root.querySelector('[data-am-ai-analysis-form]');
        const input = root.querySelector('[data-am-ai-analysis-input]');
        const send = root.querySelector('[data-am-ai-analysis-send]');
        const count = root.querySelector('[data-am-ai-analysis-count]');
        const messages = root.querySelector('[data-am-ai-analysis-messages]');
        const status = root.querySelector('[data-am-ai-analysis-status]');
        const error = root.querySelector('[data-am-ai-analysis-error]');
        const endpoint = root.getAttribute('data-endpoint');
        const birthchartHash = root.getAttribute('data-birthchart-hash');

        if (
            !form
            || !input
            || !send
            || !count
            || !messages
            || !status
            || !error
            || !endpoint
            || !birthchartHash
            || request === null
        ) {
            return false;
        }

        const maxLength = Number.isInteger(input.maxLength) && input.maxLength > 0
            ? input.maxLength
            : 500;
        const updateCount = () => {
            count.textContent = `${input.value.length}/${maxLength}`;
        };
        const setBusy = (busy) => {
            root.setAttribute('aria-busy', busy ? 'true' : 'false');
            input.disabled = busy;
            send.disabled = busy;
        };
        const clearError = () => {
            error.hidden = true;
            error.textContent = '';
        };

        input.addEventListener('input', updateCount);
        input.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                event.preventDefault();
                form.requestSubmit();
            }
        });

        for (const suggestion of documentObject.querySelectorAll('[data-am-ai-analysis-suggestion]')) {
            suggestion.addEventListener('click', () => {
                input.value = suggestion.textContent.trim().slice(0, maxLength);
                updateCount();
                input.focus();
            });
        }

        form.addEventListener('submit', async (event) => {
            event.preventDefault();
            const question = input.value.trim();
            if (question === '') {
                input.focus();
                return;
            }

            clearError();
            setBusy(true);
            status.textContent = 'Preparando uma resposta sobre o seu mapa…';

            const body = {
                birthchart_hash: birthchartHash,
                entry_point: 'unknown',
                message: question,
                type: 'birthchart',
            };
            const telemetryNonce = root.getAttribute('data-telemetry-nonce');
            if (TELEMETRY_NONCE_PATTERN.test(telemetryNonce ?? '')) {
                body.telemetry_nonce = telemetryNonce;
            }

            try {
                const response = await request(endpoint, {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify(body),
                    credentials: 'same-origin',
                    referrerPolicy: 'no-referrer',
                });
                let payload = null;
                try {
                    payload = await response.json();
                } catch {
                    // The consolidated error below covers malformed responses.
                }

                const normalized = response.ok ? responsePayload(payload) : null;
                if (!response.ok || normalized === null || normalized.requiresPremium) {
                    throw new Error(requestErrorMessage(response.status, payload));
                }

                appendMessage(messages, 'user', question);
                appendMessage(messages, 'assistant', normalized.response);
                deliverReplyPresented(request, telemetryNonce);
                input.value = '';
                updateCount();
                status.textContent = 'Resposta recebida. Você pode continuar a conversa.';
            } catch (requestError) {
                error.textContent = requestError instanceof Error
                    ? requestError.message
                    : requestErrorMessage(0, null);
                error.hidden = false;
                status.textContent = '';
            } finally {
                setBusy(false);
                input.focus();
            }
        });

        scrollMessagesToLatest(messages);
        updateCount();

        return true;
    }

    if (typeof document !== 'undefined') {
        if (document.readyState === 'loading') {
            document.addEventListener(
                'DOMContentLoaded',
                () => initBirthchartAIAnalysis(),
                {once: true},
            );
        } else {
            initBirthchartAIAnalysis();
        }
    }

    if (typeof module !== 'undefined' && module.exports) {
        module.exports = {
            appendMessage,
            deliverReplyPresented,
            initBirthchartAIAnalysis,
            requestErrorMessage,
            responsePayload,
        };
    }
})();
